import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classnames from 'classnames'
import { AdjustmentsHorizontalIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import {
  DEFAULT_HEADER_CHIP_IDS,
  HEADER_CHIP_IDS,
  HEADER_CHIP_LABELS,
  type HeaderChipId,
} from './headerChipPrefs'

export interface HeaderChipOverflowItem {
  id: HeaderChipId
  node: React.ReactNode
}

export interface HeaderChipsPickerProps {
  pinnedChipIds: readonly HeaderChipId[]
  onToggle: (id: HeaderChipId) => void
  onReset: () => void
  onOpenChange?: (open: boolean) => void
  /** Pinned chips that do not fit inline — shown above the customize list. */
  overflowChips?: readonly HeaderChipOverflowItem[]
  /** Current values for every chip id (shown in More + Customize rows). */
  chipValues?: Partial<Record<HeaderChipId, React.ReactNode>>
}

const menuPositionFromButton = (button: HTMLButtonElement): { top: number; right: number } => {
  const rect = button.getBoundingClientRect()
  return { top: rect.bottom + 6, right: window.innerWidth - rect.right }
}

/** Compact popup to pin/unpin account header chips — lives next to the chip row. */
export const HeaderChipsPicker: React.FC<HeaderChipsPickerProps> = ({
  pinnedChipIds,
  onToggle,
  onReset,
  onOpenChange,
  overflowChips = [],
  chipValues = {},
}) => {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null)
  const pinned = new Set(pinnedChipIds)
  const isDefaultPinned =
    pinnedChipIds.length === DEFAULT_HEADER_CHIP_IDS.length &&
    DEFAULT_HEADER_CHIP_IDS.every((id, index) => pinnedChipIds[index] === id)
  const hasOverflow = overflowChips.length > 0

  const placeMenu = useCallback(() => {
    const button = buttonRef.current
    if (button == null) return
    setPos(menuPositionFromButton(button))
  }, [])

  const setMenuOpen = useCallback(
    (next: boolean): void => {
      if (next) {
        const button = buttonRef.current
        if (button != null) setPos(menuPositionFromButton(button))
      } else {
        setPos(null)
      }
      setOpen(next)
      onOpenChange?.(next)
    },
    [onOpenChange]
  )

  useLayoutEffect(() => {
    if (!open) return
    placeMenu()
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target) === true) return
      if (menuRef.current?.contains(target) === true) return
      setMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', placeMenu)
    window.addEventListener('scroll', placeMenu, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', placeMenu)
      window.removeEventListener('scroll', placeMenu, true)
    }
  }, [open, placeMenu, setMenuOpen])

  const buttonLabel = hasOverflow ? 'More header chips' : 'Customize header chips'

  return (
    <div
      className="relative shrink-0 self-center"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={buttonLabel}
        title={hasOverflow ? 'More chips & customize header' : 'Customize header chips'}
        onClick={() => setMenuOpen(!open)}
        className={classnames(
          'relative inline-flex h-7 w-7 items-center justify-center rounded-md border text-slate-500',
          'hover:border-slate-300 hover:bg-white/70 hover:text-slate-800',
          'dark:text-slate-400 dark:hover:border-slate-600 dark:hover:bg-slate-800/70 dark:hover:text-slate-200',
          hasOverflow || open
            ? 'border-slate-300 bg-white/70 dark:border-slate-600 dark:bg-slate-800/70'
            : 'border-transparent'
        )}
      >
        <AdjustmentsHorizontalIcon className="h-4 w-4" aria-hidden />
        {hasOverflow ? (
          <span
            className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-indigo-500"
            aria-hidden
          />
        ) : null}
      </button>
      {open && pos != null
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              aria-label="Header chips"
              className="fixed z-[200] w-72 rounded-md border border-slate-200 bg-white py-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-800"
              style={{ top: pos.top, right: pos.right }}
            >
              {hasOverflow ? (
                <div className="border-b border-slate-200 px-1.5 pb-1.5 dark:border-slate-700">
                  <div className="px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    More chips
                  </div>
                  <ul className="max-h-48 space-y-0.5 overflow-y-auto px-1">
                    {overflowChips.map((item) => (
                      <li
                        key={item.id}
                        role="none"
                        className="flex min-w-0 items-center justify-between gap-2 rounded px-1.5 py-1"
                      >
                        <span className="min-w-0 shrink truncate text-xs text-slate-700 dark:text-slate-200">
                          {HEADER_CHIP_LABELS[item.id]}
                        </span>
                        <div className="flex min-w-0 shrink-0 justify-end">{item.node}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="max-h-80 space-y-0.5 overflow-auto px-1.5 pt-1">
                {hasOverflow ? (
                  <div className="px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    Customize
                  </div>
                ) : null}
                {HEADER_CHIP_IDS.map((id) => {
                  const isPinned = pinned.has(id)
                  const label = HEADER_CHIP_LABELS[id]
                  const ToggleIcon = isPinned ? MinusIcon : PlusIcon
                  const value = chipValues[id]
                  return (
                    <div
                      key={id}
                      className={classnames(
                        'flex items-center gap-2 rounded px-1.5 py-1',
                        isPinned && 'opacity-55'
                      )}
                    >
                      <span
                        className={classnames(
                          'min-w-0 flex-1 truncate text-xs',
                          isPinned
                            ? 'text-slate-400 dark:text-slate-500'
                            : 'text-slate-700 dark:text-slate-200'
                        )}
                      >
                        {label}
                      </span>
                      {value != null ? (
                        <div className="flex shrink-0 justify-end">
                          {value}
                        </div>
                      ) : null}
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => onToggle(id)}
                        aria-label={isPinned ? `Remove ${label} from header` : `Add ${label} to header`}
                        title={isPinned ? 'Remove from header' : 'Add to header'}
                        className={classnames(
                          'inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border transition-colors',
                          isPinned
                            ? 'border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-500 dark:hover:text-slate-300'
                            : 'border-indigo-200 bg-indigo-50 text-indigo-600 hover:border-indigo-300 hover:bg-indigo-100 dark:border-indigo-500/40 dark:bg-indigo-500/15 dark:text-indigo-300 dark:hover:bg-indigo-500/25'
                        )}
                      >
                        <ToggleIcon className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </div>
                  )
                })}
              </div>
              <div className="mt-1 border-t border-slate-200 px-1.5 pt-1.5 dark:border-slate-700">
                <button
                  type="button"
                  onClick={onReset}
                  disabled={isDefaultPinned}
                  className="w-full rounded px-1.5 py-1 text-left text-xs font-medium text-indigo-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 dark:text-indigo-400 dark:hover:bg-slate-700/60 dark:disabled:text-slate-500"
                >
                  Reset to defaults
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  )
}

export default HeaderChipsPicker
